/**
 * PaperSheetView — 지면 칸의 얼굴. 계산(본문·측정·배치·정답·배율)은 usePaperSheet 가 하고, 이 파일은 그 결과를 그린다.
 *
 *   PaperSheetView       — 머리(안내 · 맞춤·−·%·+ · [미리보기] · 본문 진행) + 지면이 앉는 스크롤 칸
 *   PaperSheetHandle     — 서랍의 「지면」 손잡이(겉껍질의 왼쪽 밖에 붙어 서랍과 함께 미끄러진다)
 *   PaperSheetFullPreview — 전체화면 인쇄 미리보기. 창(Modal) **밖**에 둔다 — Tab 가두기·겹침이 창과 따로 논다
 *
 * 시험지 조립 창과 학생 화면 「시험지 불러오기」 창이 같이 쓴다(오너 2026-09-14). 서랍을 어디에 어떻게 세울지
 * (열로 설지·서랍일지·언제 열지)는 부르는 창이 정한다.
 */
import React, { lazy, Suspense } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../common';
import PrintSheetV4 from '../../print/PrintSheetV4';

// 전체화면 [미리보기]는 관례대로 lazy 로 둔다. **다만 아끼는 것은 없다** — 그 알맹이는 이미 본 번들에 있다(usePaperSheet 머리 주석).
const PrintPreviewModal = lazy(() => import('../printPreview').then((m) => ({ default: m.PrintPreviewModal })));

/**
 * 지면은 문항 구성이 바뀔 때만 다시 그린다.
 * PrintSheetV4 는 면마다 dangerouslySetInnerHTML 로 조판된 본문을 뿌린다 — 제목 타이핑이나
 * 검색 결과 도착이 그 렌더를 끌고 다니면 글자마다 화면이 멎으므로 여기서 끊는다.
 * 배율(zoom)도 이 방벽 **바깥**에 둔다 — 배율은 CSS 한 줄이지 조판이 아니다.
 */
// 고정 참조 — 인라인 지면은 풀이 면을 그리지 않는다. 매 렌더 새 배열을 넘기면 그것만으로
// memo 가 뚫린다. PrintSheetV4 는 세 배열을 전부 **필수**로 읽으므로 생략할 수 없다.
const NO_SOLUTIONS = [];

const PagePreview = React.memo(function PagePreview({ problemPages, answers, geo, meta }) {
  return (
    <PrintSheetV4
      problemPages={problemPages}
      answers={answers}
      solutionPages={NO_SOLUTIONS}
      geo={geo}
      meta={meta}
    />
  );
});

/** 서랍 손잡이 — 겉껍질의 **왼쪽 밖**(right-full)에 붙어 서랍과 함께 미끄러진다.
    닫히면 겉껍질이 폭만큼 밀려나므로 손잡이 오른쪽 끝이 상자 오른쪽 끝에 정확히 맞는다
    (부르는 쪽이 pr-9 로 비워 둔 자리다). 위치를 따로 계산하지 않아도 되는 이유다. */
export function PaperSheetHandle({
  open, onToggle, controls, hidden = false, openTitle = '실제 지면을 폅니다',
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      aria-label={open ? '지면 미리보기 닫기' : '지면 미리보기 열기'}
      title={open ? '지면을 접습니다' : openTitle}
      /* 초점 테두리가 초록인데 버튼 채움도 초록이다 — 사이에 흰 간격(ring-offset-2)을
         두지 않으면 초점이 어디 있는지 보이지 않는다. 1px 로는 모자라 2px 이다. */
      className={`absolute right-full top-1/2 ${hidden ? 'hidden' : 'flex'} -translate-y-1/2 flex-col items-center gap-1 rounded-l-full bg-primary py-3 pl-2.5 pr-2 text-white shadow-lg transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
    >
      {open
        ? <ChevronRight aria-hidden="true" className="size-4" />
        : <ChevronLeft aria-hidden="true" className="size-4" />}
      {/* 세로쓰기 — 띠가 36px 이라 가로로는 두 글자도 못 앉는다. 한글은 세로로 읽히는
          글자라 회전이 아니라 세로쓰기로 세운다(회전하면 글자가 눕는다).
          12px 은 이 저장소 글자 6단의 하한이다 */}
      <span className="text-[12px] font-bold leading-none [writing-mode:vertical-rl]">지면</span>
    </button>
  );
}

/** 전체화면 [미리보기] — 창 Modal 밖에 둔다. 인쇄 오버레이(9999990)가 두 창(9999960·9999970)보다 위다 */
export function PaperSheetFullPreview({ sheet }) {
  if (!sheet.previewOpen || !sheet.previewData) return null;
  return (
    <Suspense fallback={null}>
      <PrintPreviewModal
        isOpen
        onClose={sheet.closePreview}
        testData={sheet.previewData}
        isMobileView={window.innerWidth < 768}
      />
    </Suspense>
  );
}

/**
 * hint        — 머리 왼쪽 안내 한 줄
 * emptyText   — 문항이 없을 때 칸 가운데 글
 * onPaperClick — 지면 문항 클릭(조립 창의 교체). 주면 문항에 손가락 커서가 선다
 */
export default function PaperSheetView({
  sheet, hint, emptyText, onPaperClick = null,
}) {
  const {
    boxRef, count, problemPages, answers, geo, sheetMeta, scale, zoom, stepZoom, fitToWidth,
    missingCount, failedCount, retryContents, previewOpen, openPreview,
  } = sheet;
  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b px-3 py-2">
        <p className="min-w-0 flex-1 truncate text-[11px] text-gray-400">{hint}</p>
        {/* 지면은 칸보다 훨씬 넓어 축소가 기본이다 — 본문을 읽으려면 키운다.
            기본값(맞춤)은 가로 스크롤이 나지 않는 가장 큰 배율이고, 칸 폭이 바뀌면 다시 맞춘다 */}
        <div className="flex shrink-0 items-center gap-0.5 text-[11px] text-gray-500">
          {/* 맞춤이 −·+ 왼쪽에 선다(오너 지시). 손으로 배율을 만졌을 때만 나타나므로
              −·%·+ 는 제자리에 있고, 이 그룹이 왼쪽으로 늘어나며 옆 문단이 그만큼 줄어든다 */}
          {zoom != null && (
            <button
              type="button"
              onClick={fitToWidth}
              title="가로 스크롤이 생기지 않는 가장 큰 배율로 맞춥니다"
              className="rounded px-1.5 py-0.5 text-primary hover:bg-primary/10"
            >
              맞춤
            </button>
          )}
          <button
            type="button"
            onClick={() => stepZoom(-0.05)}
            disabled={count === 0}
            aria-label="지면 축소"
            className="rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-30"
          >
            −
          </button>
          <span className="w-9 text-center tabular-nums">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={() => stepZoom(0.05)}
            disabled={count === 0}
            aria-label="지면 확대"
            className="rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-30"
          >
            +
          </button>
        </div>
        <Button size="xs" variant="secondary" onClick={openPreview} disabled={count === 0}>
          <Eye className="mr-0.5 inline size-3" />
          미리보기
        </Button>
        {/* 본문이 도착하는 동안에도 직전 지면은 그대로 둔다 — 여기 진행 표시만 얹는다 */}
        <p
          role="status"
          aria-live="polite"
          className={missingCount > 0 || failedCount > 0 ? 'w-full text-[11px] text-gray-500' : 'sr-only'}
        >
          {missingCount > 0 && `본문 ${missingCount}개를 불러오는 중…`}
          {missingCount === 0 && failedCount > 0 && (
            <>
              <span className="text-danger">본문 {failedCount}개를 불러오지 못했습니다.</span>
              <button type="button" onClick={retryContents} className="ml-1 underline hover:text-gray-800">
                다시 시도
              </button>
            </>
          )}
        </p>
      </div>
      {/* ref 는 콜백(boxRef) — 칸이 붙는 순간 훅이 폭 감시를 건다(늦게 붙어도 놓치지 않게) */}
      <div ref={boxRef} className="min-h-0 flex-1 overflow-auto bg-gray-200 p-3">
        {count === 0 && (
          <p className="mx-auto mt-8 max-w-xs rounded-xl border border-dashed border-gray-300 bg-white/70 p-6 text-center text-xs text-gray-400">
            {emptyText}
          </p>
        )}
        {/* 전체화면 미리보기가 떠 있는 동안은 내린다 — sheetChromeCss 의 .pv4-page 는
            조상에 매이지 않은 **문서 전역** 규칙이라, 용지가 다른 시트 둘이 공존하면
            문서 순서상 뒤(오버레이 포털)가 뒤에 가린 이쪽까지 지배한다. 그 사이 넘침
            보정이 돌면 다른 용지의 폭이 naturalRef 에 굳는다. 어차피 덮여 보이지 않고,
            닫히면 잰 값 그대로 다시 선다. */}
        {count > 0 && !previewOpen && (
          <>
            {problemPages.length === 0 && (
              <p className="py-10 text-center text-xs text-gray-500">지면을 준비하는 중…</p>
            )}
            {/* 배율은 transform 이 아니라 CSS zoom — 레이아웃 박스가 실제로 줄어야
                부모의 overflow-auto 가 살고 '넘쳤는지'를 잴 수 있다. memo 바깥이라
                배율을 만져도 지면이 다시 조판되지 않는다.
                지면은 문항이 아직 안 앉았을 때도 마운트한다 — 그래야 SheetChrome 이
                첫 프레임부터 @font-face 를 문서에 붙들어, 측정의 폰트 검사가 성립한다
                (빈 시트는 자식이 없어 화면에 아무것도 그리지 않는다). */}
            <div
              className={onPaperClick ? '[&_.pv4-problem-item]:cursor-pointer' : undefined}
              style={{ zoom: scale }}
              onClick={onPaperClick || undefined}
            >
              <PagePreview
                problemPages={problemPages}
                answers={answers}
                geo={geo}
                meta={sheetMeta}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
